import React, { useState, useCallback } from "react"
import { Button, TextField } from "letalkui"
import { Grid } from "@material-ui/core"
import { Add as AddIcon } from "@material-ui/icons"

import { InputDialog, Loading, Notification, PageHeader } from "@/components"

import useDidMount from "@/hooks/useDidMount"

import ErrorHandlerService from "@/services/ErrorHandler"
import AgentService, { AgentFormattedForList, UpdateAgentProfileInput } from "@/services/AgentService"

import { ErrorType } from "@/hooks/useValidation"

import CreateAgentDialog from "@/pages/Admin/Agent/AgentList/CreateAgentDialog"
import EmptyAgentList from "@/pages/Admin/Agent/AgentList/EmptyAgentList"
import AgentListItem from "@/pages/Admin/Agent/AgentList/AgentListItem"

import AgentListSkeleton from "@/skeletons/Admin/AgentListSkeleton"

const AgentList: React.FC = () => {
	const [loading, setLoading] = useState<boolean>(false)
	const [agents, setAgents] = useState<AgentFormattedForList[]>([])

	const handleGetAgents = useCallback(async () => {
		setLoading(true)

		try {
			const agents = await AgentService.retrieveAllAgentsFormattedForList()

			setAgents(agents)
		} catch (error) {
			ErrorHandlerService.handle(error as ErrorType)
		}

		setLoading(false)
	}, [])

	const handleUpdateAgentName = (agentId: number, newName: string) => {
		setAgents(prev => prev.map(agent => agent.id === agentId ? { ...agent, name: newName } : agent))
	}

	const handleEditAgentProfile = (agentId: number, currentAgentProfile: UpdateAgentProfileInput["data"]) => {
		InputDialog.open({
			title: "Alterar nome do Agente",
			onOk: async (newAgentName) => {
				try {
					await AgentService.updateAgentProfile({
						agentId: agentId,
						data: {
							...currentAgentProfile,
							name: newAgentName as string
						}
					})

					handleUpdateAgentName(agentId, newAgentName as string)
					Notification.success({ message: "Nome do Agente alterado com sucesso!" })

					return true
				} catch (error) {
					ErrorHandlerService.handle(error as ErrorType)
					Notification.error({ message: "Não foi possível alterar o nome do Agente." })
					return false
				}
			},
			initialValue: currentAgentProfile.name,
			fullWidth: true,
			customInputElement: (
				<TextField
					label="Nome"
					subLabel="Nome que o agente irá se identificar para o cliente durante o atendimento."
					placeholder="Ex: João da Silva"
					type="text"
					value={currentAgentProfile.name}
					fullWidth
				/>
			)
		})
	}

	const agentListContent = () => {
		const hasAgentsToList = agents?.length > 0

		if (!loading && !hasAgentsToList) {
			return <EmptyAgentList />
		}

		return (
			<Loading loading={loading} customLoadingElement={<AgentListSkeleton />}>
				<Grid container spacing={2}>
					{agents?.map(agent => (
						<Grid item xs={12} key={agent.id}>
							<AgentListItem
								agent={agent}
								onEditAgentProfile={() => handleEditAgentProfile(agent.id, {
									name: agent.name,
									designation: agent.designation
								})}
							/>
						</Grid>
					))}
				</Grid>
			</Loading>
		)
	}

	useDidMount(() => {
		handleGetAgents()
	})

	return (
		<Grid container direction="column" spacing={8}>
			<Grid item xs={12}>
				<PageHeader
					title="Agentes de IA"
					breadcrumb={{
						name: "Agentes de IA",
						pathname: "/admin/ai/agent"
					}}
					action={
						<CreateAgentDialog>
							<Button.Primary
								size="small"
								startIcon={<AddIcon />}
							>
								Adicionar Agente
							</Button.Primary>
						</CreateAgentDialog>
					}
				/>
			</Grid>

			<Grid item xs={12}>
				{agentListContent()}
			</Grid>
		</Grid>
	)
}

export default AgentList
